export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again to continue.';
export const SESSION_UNAVAILABLE_MESSAGE = 'Your session could not be verified. Check your connection and retry. Your entered details remain on this device where available.';
export type SessionState = 'restoring' | 'authenticated' | 'anonymous' | 'unavailable' | 'expired';
export type SessionFailure = {
  source: 'rest' | 'refresh';
  kind: 'access-expired' | 'invalid-jwt' | 'missing-jwt' | 'terminal' | 'transient' | 'configuration';
  code: string;
  status: number;
};
const terminal = new Set(['refresh_token_not_found', 'refresh_token_already_used', 'session_expired', 'session_not_found']);
export function sessionErrorKind(error: unknown): 'terminal' | 'transient' | 'other' {
  const value = error && typeof error === 'object' ? error as {
    code?: unknown;
    name?: unknown;
    status?: unknown;
  } : {};
  if (terminal.has(String(value.code)) || value.name === 'AuthSessionMissingError') return 'terminal';
  if (value.name === 'AuthRetryableFetchError' || value.name === 'SessionCheckTimeout' || Number(value.status) >= 500 || Number(value.status) === 429 || value instanceof TypeError) return 'transient';
  return 'other';
}
export function responseSessionFailure(source: 'rest' | 'refresh', status: number, body: unknown): SessionFailure | null {
  const value = body && typeof body === 'object' ? body as {
    code?: unknown;
    error_code?: unknown;
    message?: unknown;
  } : {};
  const candidate = String(value.code ?? value.error_code ?? '');
  const code = terminal.has(candidate) || ['PGRST300', 'PGRST301', 'PGRST302', 'PGRST303', 'bad_jwt', 'request_timeout', 'over_request_rate_limit'].includes(candidate) ? candidate : 'unknown';
  if (source === 'refresh') {
    if (terminal.has(code)) return {
      source,
      kind: 'terminal',
      code,
      status
    };
    if (status >= 500 || status === 429 || status === 0) return {
      source,
      kind: 'transient',
      code,
      status
    };
    return null;
  }
  if (code === 'PGRST300') return {
    source,
    kind: 'configuration',
    code,
    status
  };
  if (status !== 401) return null;
  if (code === 'PGRST302') return {
    source,
    kind: 'missing-jwt',
    code,
    status
  };
  if (['PGRST301', 'PGRST303', 'bad_jwt'].includes(code)) return {
    source,
    kind: /\b(?:JWT|token)(?: has| is)? expired\b/i.test(String(value.message)) ? 'access-expired' : 'invalid-jwt',
    code,
    status
  };
  // SQL/RLS 42501 and 403 are authorization outcomes, not refresh-token expiry.
  return null;
}
export class SessionCheckTimeout extends Error {
  constructor() {
    super('Session verification took too long.');
    this.name = 'SessionCheckTimeout';
  }
}
export function sessionDeadline<T>(work: Promise<T>, timeout = 10_000): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new SessionCheckTimeout()), timeout);
    work.then(value => {
      clearTimeout(timer);
      resolve(value);
    }, error => {
      clearTimeout(timer);
      reject(error);
    });
  });
}
/** Allow only known application paths. Never carry callback tokens or external destinations. */
export function safeReturnPath(candidate: unknown, role?: string): string {
  const fallback = role === 'admin' ? '/admin' : '/dashboard';
  if (typeof candidate !== 'string' || !candidate.startsWith('/') || candidate.startsWith('//') || /[\\\u0000-\u0020]/.test(candidate)) return fallback;
  let url: URL;
  try {
    url = new URL(candidate, 'https://return.invalid');
  } catch {
    return fallback;
  }
  if (url.origin !== 'https://return.invalid') return fallback;
  if (url.pathname === '/admin') {
    if (role === 'customer') return fallback;
    const safe = new URLSearchParams();
    for (const key of ['tab', 'riderApplicationStatus', 'ridersView', 'remittancesView', 'settingsView', 'dispatchView']) {
      const value = url.searchParams.get(key);
      if (value && /^[a-z_\-]{1,40}$/i.test(value)) safe.set(key, value);
    }
    return '/admin' + (safe.size ? '?' + safe : '');
  }
  const detail = /^\/orders\/[a-f0-9-]{36}$/i.test(url.pathname);
  if (role === 'admin' && !detail) return fallback;
  return detail || ['/dashboard', '/orders', '/orders/new', '/profile', '/notifications'].includes(url.pathname) ? url.pathname : fallback;
}
